/**
 * Asset insertion helpers
 *
 * Converts a library AssetRecord into a CanvasElement ready for
 * studioStore.addElement(). Used by both double-click insert from the panel
 * and native drag-and-drop from the panel onto the canvas.
 */
import type { AssetRecord, CanvasElement, ImageElement, SvgElement } from '../types';
import { getAssetPublicUrl, useAssetStore } from '../stores/assetStore';

export interface InsertPosition {
  x: number;
  y: number;
}

export interface InsertContext {
  canvasWidth: number;
  canvasHeight: number;
  elementsCount: number;
  position?: InsertPosition;
  maxDimension?: number;
}

export async function buildElementFromAsset(
  asset: AssetRecord,
  ctx: InsertContext
): Promise<CanvasElement | null> {
  const maxDim = ctx.maxDimension ?? Math.min(400, ctx.canvasWidth * 0.6);
  const origW = asset.width || maxDim;
  const origH = asset.height || maxDim;
  const longest = Math.max(origW, origH);
  const scale = longest > maxDim ? maxDim / longest : 1;
  const width = Math.max(1, Math.round(origW * scale));
  const height = Math.max(1, Math.round(origH * scale));

  const x = Math.round(
    (ctx.position ? ctx.position.x - width / 2 : (ctx.canvasWidth - width) / 2)
  );
  const y = Math.round(
    (ctx.position ? ctx.position.y - height / 2 : (ctx.canvasHeight - height) / 2)
  );

  const base = {
    id: crypto.randomUUID(),
    x,
    y,
    width,
    height,
    rotation: 0,
    opacity: 1,
    visible: true,
    locked: false,
    zIndex: ctx.elementsCount,
    layer: 'BACKGROUND_LAYER' as const,
    contentType: 'STATIC' as const,
  };

  if (asset.asset_type === 'svg') {
    let svgMarkup = asset.svg_markup;
    if (!svgMarkup) {
      try {
        const url = getAssetPublicUrl(asset.storage_path);
        const resp = await fetch(url);
        svgMarkup = await resp.text();
      } catch (err) {
        console.error('[assetInsert] failed to fetch SVG markup', err);
        return null;
      }
    }
    const svgEl: SvgElement = {
      ...base,
      type: 'svg',
      name: asset.name || 'SVG',
      svgMarkup: svgMarkup || '',
      originalWidth: origW,
      originalHeight: origH,
      preserveAspectRatio: true,
      sourceFileName: asset.file_name,
      assetId: asset.id,
    };
    return svgEl;
  }

  const src = getAssetPublicUrl(asset.storage_path);
  const imageEl: ImageElement = {
    ...base,
    type: 'image',
    name: asset.name || 'Image',
    src,
    alt: asset.name || '',
    objectFit: 'contain',
    assetId: asset.id,
  };
  return imageEl;
}

export async function resolveAssetById(id: string): Promise<AssetRecord | null> {
  const { assets } = useAssetStore.getState();
  return assets.find((a) => a.id === id) || null;
}
